import * as React from 'react';

import { useParams } from 'js/lib/useRouter';

import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useCampusWordingForLearner } from 'bundles/unified-common/hooks/useCampusWordingForLearner';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import AssignButtonNotConfigurable from 'bundles/unified-description-page-common/components/admin/AssignButtonNotConfigurable';
import EnterpriseCourseEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/enterprise/EnterpriseCourseEnrollButtonNotConfigurable';
import HeroInstructors from 'bundles/unified-description-page-common/components/modules/hero/HeroInstructors';
import HeroLanguageInfo from 'bundles/unified-description-page-common/components/modules/hero/HeroLanguageInfo';
import HeroModule from 'bundles/unified-description-page-common/components/modules/hero/HeroModule';
import HeroPartnerLogo from 'bundles/unified-description-page-common/components/modules/hero/HeroPartnerLogo';
import HeroTotalEnrollmentCount from 'bundles/unified-description-page-common/components/modules/hero/HeroTotalEnrollmentCount';
import HeroCourseDescription from 'bundles/unified-description-page-common/components/modules/hero/course/HeroCourseDescription';
import KeyInformationHorizontalModule from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationHorizontalModule';
import LearningObjectives from 'bundles/unified-description-page-common/components/modules/product-overview/LearningObjectives';
import ProductOverviewHorizontalModule from 'bundles/unified-description-page-common/components/modules/product-overview/ProductOverviewHorizontalModule';
import Skills from 'bundles/unified-description-page-common/components/modules/product-overview/Skills';
import Details from 'bundles/unified-description-page-common/components/modules/product-overview/details/Details';
import CoBrandingPartnerLogos from 'bundles/unified-description-page-common/components/shared/CoBrandingPartnerLogos';
import { HERO_BACKGROUND_COLOR } from 'bundles/unified-description-page-common/constants';
import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';
import AboutSubmoduleRenderer from 'bundles/unified-description-page-common/module-renderers/product-overview/AboutSubmoduleRenderer';
import type { EnterpriseCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseCoursePageQuery';
import type { ProductOverviewModuleConfig } from 'bundles/unified-description-page-common/types/moduleConfig';
import { getSpecializationProductUrl } from 'bundles/unified-description-page-common/utils';
import getCourseProductDetailItems from 'bundles/unified-description-page-common/utils/getCourseProductDetailItems';
import getEnterpriseCourseKeyInformationItems from 'bundles/unified-description-page-common/utils/getEnterpriseCourseKeyInformationItems';
import { COURSE_NAV_ITEMS } from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import getPartnersForCourse from 'bundles/unified-description-page-common/utils/getPartnersForCourse';

import _t from 'i18n!nls/xdp';

type Props = {
  pageData: EnterpriseCoursePageFieldsFragment;
  config?: ProductOverviewModuleConfig;
};

export const EnterpriseCourseProductOverviewModuleRenderer = ({ pageData, config }: Props): React.ReactElement => {
  const { navigationLinks } = usePageNavigation();
  const isCampusWording = useCampusWordingForLearner();
  const productTitle = getProductTitle(pageData.name);
  const partnersToDisplay = getPartnersForCourse(pageData.partners);
  const { programSlug } = useParams();
  const features = useEnterpriseProductFeatures();

  const detailItems = getCourseProductDetailItems(pageData, config?.productDetailItems || []);

  const { items: keyInformationItems } = getEnterpriseCourseKeyInformationItems(
    pageData,
    {
      ...config?.keyInfoModule,
    },
    isCampusWording
  );
  const overviewSkills = <Skills skills={pageData.skills} />;
  const overviewDescription = <LearningObjectives learningObjectives={pageData.cmlLearningObjectives} />;
  const details = <Details detailItems={detailItems} />;

  return (
    <ProductOverviewHorizontalModule
      anchorId={COURSE_NAV_ITEMS.ABOUT}
      heroBackgroundColor={HERO_BACKGROUND_COLOR}
      navigationLinks={navigationLinks}
      hero={
        <HeroModule
          productTitle={productTitle}
          backgroundImage={config?.backgroundImage}
          partnerLogo={
            partnersToDisplay && partnersToDisplay.length > 1 ? (
              <CoBrandingPartnerLogos partners={partnersToDisplay} isNavPlacement={false} />
            ) : (
              <HeroPartnerLogo image={partnersToDisplay?.[0]?.primaryLogo} partnerName={partnersToDisplay?.[0]?.name} />
            )
          }
          description={
            <React.Fragment>
              {pageData.primarySpecialization && (
                <HeroCourseDescription
                  primarySpecialization={pageData.primarySpecialization}
                  primarySpecializationLink={getSpecializationProductUrl({
                    productSlug: pageData.primarySpecialization.slug,
                    productVariant: pageData.primarySpecialization.productVariant,
                  })}
                  specializations={pageData.specializations}
                />
              )}
              {config?.showHeroLanguageInfo && (
                <HeroLanguageInfo
                  languages={{
                    primaryLanguages: pageData.primaryLanguages,
                    subtitleLanguages: pageData.subtitleLanguages ?? [],
                    translatedLanguages: pageData.translatedLanguages ?? [],
                    dubbedLanguages: pageData.dubbedLanguages ?? [],
                  }}
                />
              )}
            </React.Fragment>
          }
          instructors={
            <HeroInstructors
              instructors={pageData.instructors}
              averageInstructorRating={pageData.averageInstructorRating}
              instructorRatingCount={pageData.instructorRatingCount}
              partners={pageData.partners}
              disableCTA={config?.removeLinksAboveFold}
              displayTopInstructorBadgeInline
            />
          }
          primaryCta={
            <EnterpriseCourseEnrollButtonNotConfigurable
              courseType={pageData.courseType}
              productId={pageData.id}
              slug={pageData.slug}
              shouldShowSponsoredByMessage={features?.shouldShowSponsoredByMessage}
              // eslint-disable-next-line no-restricted-syntax
              buttonLabel={_t('Go To Course')}
            />
          }
          secondaryCta={
            config?.showAssignButton && (
              <AssignButtonNotConfigurable productSlug={pageData.slug} programSlug={programSlug} productType="course" />
            )
          }
          additionalInfo={<HeroTotalEnrollmentCount count={pageData.totalEnrollmentCount} />}
        />
      }
      keyInformation={<KeyInformationHorizontalModule keyInformationItems={keyInformationItems} />}
      aboutSubmodule={
        <AboutSubmoduleRenderer
          skills={overviewSkills}
          description={overviewDescription}
          details={details}
          config={config}
        />
      }
    />
  );
};

export default EnterpriseCourseProductOverviewModuleRenderer;
